'use client';

import CustomBadge from '@/components/custom-badge';
import Badge from '@/components/custom-badge/badge';
import CustomButton from '@/components/custom-button';
import {
    PROCEDURE_FIELDS_PARAM_KEY,
    PROCEDURE_TYPE_PARAM_KEY,
} from '@/components/custom-layout/customer-v2/type';
import CustomQuillEditor from '@/components/custom-quill-editor';
import TableAG, {
    ColDef,
    initialPageInfo,
    TableAGRef,
} from '@/components/custom-table-ag';
import { configKeepData5Mins } from '@/config/useQuery';
import { pageDirectories, pageDirectoryParameter } from '@/constant/route';
import { useQueryField } from '@/hooks/query/use-query-field';
import { useProcedureSelectionReady } from '@/hooks/query/use-procedure-selection-ready';
import { useQueryShowUiProcedureType } from '@/hooks/query/use-query-procedure-type-tree';
import { useTablePageInfo } from '@/hooks/use-table-page-info';
import { useURLState } from '@/hooks/use-url-state';
import { cn, nameof, wrapQuote } from '@/lib/utils';
import { useRouter } from '@/navigation';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { ProcedureResponse } from '@/server/api/v1/procedure/type';
import {
    FilterBuilders,
    FilterZodSchema,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { ProcessingMethod, SortValue } from '@/types/enum';
import { keepPreviousData } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BadgeGroupImplementSubjectView } from './badge-group-implement-subject';
import { FilterProps, ProcedureFilter } from './procedure-filter';
import { createFilterProcedureCust } from './utils';

function onSearch(keyword: string, exendFilter?: FilterProps) {
    let filters: FilterBuilders<ProcedureResponse>[] = [];

    if (keyword.length) {
        filters.push({
            key: 'Name',
            values: [keyword],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Regex,
        });
    }

    if (
        exendFilter?.processingMethod &&
        exendFilter?.processingMethod !== 'all'
    ) {
        filters.push({
            key: 'ProcessingMethod',
            values: [exendFilter.processingMethod],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        });
    }

    return filters;
}

const ProcedureCust = () => {
    const transPage = useTranslations('ProcedureManagement'); // language của trang
    const transDef = useTranslations('System'); // language dùng chung
    const _tableRef = useRef<TableAGRef>(null);
    const router = useRouter();

    const authData = useAuthenticationStore((x) => x.auth);

    const {
        isInited,
        onChangeSearch,
        pageInfo,
        setRowSelected,
        setPageInfo,
        extendFilter,
    } = useTablePageInfo<any, FilterProps>(
        {
            ...initialPageInfo,
            sorts: [
                {
                    key: nameof<ProcedureResponse>('FieldCodes'),
                    value: SortValue.ASC,
                },
            ],
        },
        true,
        true,
        onSearch,
        // Shape mặc định để hook đọc ext từ URL khi mount (convertExtFilterFromURL
        // chỉ chạy khi initExtendFilter != null). Không có dòng này thì extendFilter
        // luôn undefined -> ProcedureFilter fallback 'all' -> ghi đè URL.
        { processingMethod: 'all', searchStr: '' }
    );

    const [selectedFieldCodes] = useURLState(PROCEDURE_FIELDS_PARAM_KEY);

    /**
     * Đọc p-types từ query string (vd: ?p-types=student,visitor)
     * Ưu tiên p-types từ URL; nếu không có thì dùng performBy truyền vào qua prop (cho các route cũ như /student, /staff)
     */
    const [pTypeParam] = useURLState(PROCEDURE_TYPE_PARAM_KEY);

    // Đổi p-type giờ là shallow update (không remount trang nữa), nên KHÔNG
    // còn tự reset phân trang như khi điều hướng Next trước đây. Khi p-type
    // đổi -> về trang 1 để tránh đang ở trang N mà tập kết quả mới ngắn hơn
    // => hiển thị rỗng. Bỏ qua lần mount đầu (đã đọc page từ URL).
    const prevPTypeRef = useRef(pTypeParam);
    useEffect(() => {
        if (prevPTypeRef.current === pTypeParam) return;
        prevPTypeRef.current = pTypeParam;
        setPageInfo((p) => ({ ...p, pageNumber: 1 }));
    }, [pTypeParam, setPageInfo]);

    const { data: apiField } = useQueryField();
    const { data: apiProcedureType } = useQueryShowUiProcedureType();

    // Chờ vùng "Danh sách thủ tục" (sub-header) load xong cây loại thủ tục VÀ
    // áp xong p-types mặc định vào URL trước khi bảng fetch. Tránh lần fetch
    // "tất cả" thừa ở paint đầu (URL chưa có p-types) rồi fetch lại lần 2 khi
    // default được áp → tránh nhấp nháy / dữ liệu sai thoáng qua.
    const { isReady: isSelectionReady } = useProcedureSelectionReady();
    // Latch: một khi đã sẵn sàng thì giữ luôn — các thao tác chọn p-type sau
    // đó của user (kể cả "Tất cả" = xoá p-types) KHÔNG làm bảng disable lại.
    const hasFetchEnabledRef = useRef(false);
    if (isSelectionReady) hasFetchEnabledRef.current = true;
    const canFetch = !!isInited && hasFetchEnabledRef.current;

    // Mốc thời gian "now" cho filter (vd: EffectiveDate <= now). Cố định 1 lần
    // theo lifetime của component để memo bên dưới không tạo key mới mỗi lần
    // recompute -> tránh useQuery refetch thừa khi apiProcedureType async về.
    const nowRef = useRef(new Date().toISOString());

    /*
     * Memo hóa danh sách filter gửi lên API. Chỉ tính lại khi MỘT trong các
     * dependency thực sự đổi (loại đối tượng, auth, p-types, filter trang, lĩnh vực).
     * Tránh tạo mảng filter mới mỗi render → tránh đổi input của useQuery →
     * tránh refetch + re-render bảng liên tục (nguyên nhân giật lag trước đây).
     */
    const filterProcedureCust = useMemo<FilterZodSchema[]>(
        () => [
            ...(pageInfo.filters ?? []),
            ...createFilterProcedureCust({
                authData: authData,
                now: nowRef.current,
                fieldCodes: selectedFieldCodes,
                performBy: pTypeParam
                    ? pTypeParam.split(',').filter(Boolean) // tách chuỗi "1,2,3" thành ["1","2","3"]
                    : [],
                procedureTypes: apiProcedureType,
            }),
        ],
        [
            apiProcedureType,
            authData,
            pTypeParam,
            pageInfo.filters,
            selectedFieldCodes,
        ]
    );

    const {
        data: apiProcedure,
        isLoading,
        isFetching,
    } = api.procedure.GetValidProceduresNow_Page.useQuery(
        {
            ...pageInfo,
            filters: filterProcedureCust,
        },
        {
            ...configKeepData5Mins,
            // Giữ NGUYÊN data của p-type trước trong lúc fetch p-type mới.
            // Không có dòng này: đổi p-type -> query key đổi -> data = undefined
            // -> isLoading=true -> bảng bị xóa trắng + spinner rồi vẽ lại toàn bộ
            // => giật mạnh. Với keepPreviousData: bảng cũ giữ nguyên, AG Grid
            // delta-update theo getRowId khi data mới về => chuyển mượt.
            placeholderData: keepPreviousData,
            // Đã init filter từ URL VÀ p-types đã ổn định (sub-header resolve
            // xong). KHÔNG yêu cầu pTypeParam > 0: khi không có default thì
            // p-types rỗng = lấy TẤT CẢ thủ tục (performBy rỗng ->
            // createFilterProcedureCust không thêm filter Types_Token).
            enabled: canFetch,
        }
    );

    // Trong lúc chờ p-types ổn định, query bị disabled nên `isLoading` của
    // react-query = false → bảng sẽ hiện "Không có dữ liệu". Ghép thêm
    // `!canFetch` để bảng giữ skeleton/loading cho tới khi sẵn sàng fetch.
    const isTableLoading = !canFetch || isLoading;

    const onViewDetail = useCallback(
        (id: string) => {
            router.push(`${pageDirectories.Customer_ProcedureDetail}/${id}`);
        },
        [router]
    );

    const onFilter = useCallback(
        (filter: FilterProps) => {
            onChangeSearch(filter.searchStr, filter);
        },
        [onChangeSearch]
    );

    const columnDefs = useMemo<ColDef<ProcedureResponse>[]>(
        () => [
            {
                headerClass: 'text-base',
                headerName: 'STT',
                minWidth: 60,
                maxWidth: 60,
                cellClass: 'py-2',
                cellRenderer(params) {
                    return (
                        <div className='flex min-h-12 items-center justify-center'>
                            <span className='text-sm font-medium xl:text-base'>
                                {(params.node.rowIndex ?? 0) + 1}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerClass: 'text-base',
                headerName: transPage('ProcedureManagement_ProcedureName'),
                minWidth: 400,
                wrapText: true,
                cellClass: 'py-2',
                cellRenderer(params) {
                    return (
                        <div className='flex min-h-12 flex-col justify-center gap-1 leading-relaxed'>
                            <div
                                role='button'
                                className='cursor-pointer text-lg font-semibold text-primary hover:underline hover:opacity-80 xl:text-xl'
                                onClick={() =>
                                    onViewDetail(params.data?.ID ?? '')
                                }
                                title={transDef('System_ViewDetail')}
                            >
                                {params.data?.Name}
                            </div>

                            {params.data?.Note &&
                                params.data.Note.length > 0 && (
                                    <div className='border-collapse border-l-4 border-secondary/30 pl-4 text-sm italic text-muted-foreground'>
                                        {params.data?.Note}
                                    </div>
                                )}

                            <BadgeGroupImplementSubjectView
                                hidePreName={false}
                                types_Token={params.data?.Types_Token}
                                procedureTypes={apiProcedureType}
                            />
                        </div>
                    );
                },
                suppressMovable: true,
            },
            {
                headerClass: 'text-base [&_.ag-icon.ag-icon-filter]:text-white',
                headerName: transPage('ProcedureManagement_Field'),
                minWidth: 280,
                maxWidth: 280,
                wrapText: true,
                cellClass: 'py-2',
                cellRenderer: ({ data }) => {
                    const arrField = apiField?.filter((x) => {
                        return data?.FieldCodes?.includes(x.Code!);
                    });

                    return (
                        <div className='flex min-h-12 flex-col justify-center gap-1'>
                            {arrField?.map((x, idx) => (
                                <p key={x.ID} className='leading-relaxed'>
                                    {`${arrField.length > 1 ? `${idx + 1}. ` : ''}${x.Name}`}
                                </p>
                            ))}
                        </div>
                    );
                },
                suppressMovable: true,
            },

            {
                headerClass: 'text-base',
                headerName: transPage('ProcedureManagement_Formality'),
                minWidth: 160,
                maxWidth: 160,
                wrapText: true,
                cellClass: 'py-2 ',
                cellRenderer: (params) => {
                    const value = params.data?.ProcessingMethod;
                    return (
                        <div
                            className={cn('flex min-h-12 items-center', {
                                'text-1': value === ProcessingMethod.Offline,
                                'text-2': value === ProcessingMethod.Online,
                            })}
                        >
                            <span
                                className={cn(
                                    'min-w-20 rounded-xl px-2 py-1 text-center text-xs font-medium leading-normal',
                                    {
                                        'bg-color-1 text-color-1-foreground':
                                            value === ProcessingMethod.Offline,
                                        'bg-primary text-primary-foreground':
                                            value === ProcessingMethod.Online,
                                    }
                                )}
                            >
                                {value === ProcessingMethod.Offline
                                    ? transPage(
                                          'ProcedureManagement_ProcessingMethod_Offline'
                                      )
                                    : transPage(
                                          'ProcedureManagement_ProcessingMethod_Online'
                                      )}
                            </span>
                        </div>
                    );
                },
                suppressMovable: true,
            },
            {
                headerClass: 'text-base ',
                // headerName:  transPage('ProcedureManagement_Procedure_Handle'),
                headerName: '',
                minWidth: 180,
                maxWidth: 180,
                cellClass: 'py-2',
                cellRenderer: (params) => {
                    return (
                        <div className='flex min-h-12 items-center'>
                            <CustomButton.Root
                                onClick={() => {
                                    // Đọc slug query param từ constant để
                                    // tránh hard-code chuỗi "procedure-id"
                                    // — đổi tên trường ở 1 nơi duy nhất.
                                    const qKey =
                                        pageDirectoryParameter[
                                            pageDirectories
                                                .Customer_SubmitDossier
                                        ].procedureId;
                                    router.push(
                                        `${pageDirectories.Customer_SubmitDossier}?${qKey}=${params.data?.ID ?? ''}`
                                    );
                                }}
                                icon={<Send size={16} />}
                                variant='link'
                                className='btn-action gap-x-2 px-0 text-sm uppercase text-primary transition-all hover:scale-105 xl:text-base'
                            >
                                {transPage('ProcedureManagement_Submit')}
                            </CustomButton.Root>
                        </div>
                    );
                },
                suppressMovable: true,
            },
        ],
        [apiField, apiProcedureType, onViewDetail, router, transPage, transDef]
    );

    return (
        <div className='space-y-4 max-lg:mt-2'>
            <ProcedureFilter filter={extendFilter} onFilter={onFilter} />

            <div
                className={cn('space-y-1 transition-opacity duration-200', {
                    // Đang fetch p-type mới nhưng vẫn giữ data cũ (keepPreviousData):
                    // làm mờ nhẹ để báo loading, KHÔNG xóa bảng -> tránh giật.
                    'pointer-events-none opacity-60': isFetching && !isLoading,
                })}
            >
                {/* <h2 className='text-lg font-semibold text-primary lg:hidden'>
                    Danh sách thủ tục
                </h2> */}

                <TableAG.Root
                    ref={_tableRef}
                    showCheckbox={false}
                    // truyền thẳng query data và pageInfo vào table
                    queryData={apiProcedure}
                    pageInfo={pageInfo}
                    setPageInfo={setPageInfo}
                    // Lần tải đầu thật sự HOẶC đang chờ p-types ổn định. KHÔNG gộp
                    // isFetching để bảng không bị xóa trắng mỗi lần đổi p-type (đã
                    // có dim ở trên).
                    isLoading={isTableLoading} // loading table
                    columnDefs={columnDefs}
                    onRowDataUpdated={() => {
                        setRowSelected([]);
                    }}
                    suppressCellFocus
                    suppressRowClickSelection
                    getRowId={(params) => {
                        return params.data?.ID;
                    }}
                    headerHeight={50}
                    rowHeight={65}
                    suppressMoveWhenRowDragging
                    tableHeight={'62vh'}
                    domLayout='normal'
                />
            </div>
        </div>
    );
};

export default ProcedureCust;
